// src/components/invoices/ClientSelectionModal.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Alert,
    IconButton,
    Chip,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Info as InfoIcon,
    Save as SaveIcon,
    Check as CheckIcon
} from '@mui/icons-material';

function ClientSelectionModal({ 
    open, 
    onClose, 
    clientData,
    onSelectWithoutSave,
    onSelectAndSave
}) {
    const [isSaving, setIsSaving] = useState(false);

    if (!clientData) {
        return null;
    }

    const handleSelectWithoutSave = () => {
        onSelectWithoutSave(clientData);
    };

    const handleSelectAndSave = async () => {
        try {
            setIsSaving(true);
            await onSelectAndSave(clientData);
        } catch (error) {
            console.error('Error saving client:', error);
            setIsSaving(false);
        }
    };

    const isLocalClient = clientData.source === 'local';
    const isAfipClient = clientData.source === 'afip';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: isLocalClient ? '#4caf50' : '#2196f3',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isLocalClient ? <CheckCircleIcon /> : <InfoIcon />}
                    <Typography variant="h6" component="div">
                        {isLocalClient ? 'Cliente Registrado' : 'Cliente Encontrado en AFIP'}
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={onClose}
                    aria-label="close"
                    disabled={isSaving}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {isLocalClient && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        Este cliente ya está registrado en su base de datos.
                    </Alert>
                )}

                {isAfipClient && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Cliente encontrado en AFIP/ARCA. Puede usarlo sin guardar o guardarlo para futuras facturas.
                    </Alert>
                )}

                {/* Client Information */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Información del Cliente
                    </Typography>

                    <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        p: 2,
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1
                    }}>
                        {/* Razón Social */}
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Razón Social / Nombre
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {clientData.razon_social || 'N/A'}
                            </Typography>
                        </Box>

                        {/* CUIT */}
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                CUIT
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {clientData.documento_nro || 'N/A'}
                            </Typography>
                        </Box>

                        {/* Condición Impositiva */}
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                Condición Impositiva
                            </Typography>
                            <Box sx={{ mt: 0.5 }}>
                                <Chip 
                                    label={clientData.condicion_impositiva || 'N/A'}
                                    color={
                                        clientData.condicion_impositiva === 'RESPONSABLE INSCRIPTO' ? 'success' :
                                        clientData.condicion_impositiva === 'MONOTRIBUTO' ? 'info' :
                                        clientData.condicion_impositiva === 'EXENTO' ? 'warning' :
                                        'default'
                                    }
                                    size="small"
                                />
                            </Box>
                        </Box>

                        <Divider />

                        {/* Dirección */}
                        {clientData.direccion && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Dirección
                                </Typography>
                                <Typography variant="body2">
                                    {clientData.direccion}
                                    {clientData.localidad && `, ${clientData.localidad}`}
                                    {clientData.provincia && `, ${clientData.provincia}`}
                                    {clientData.codigopostal && ` (${clientData.codigopostal})`}
                                </Typography>
                            </Box>
                        )}

                        {/* Estado */}
                        {clientData.estado && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Estado en AFIP
                                </Typography>
                                <Typography variant="body2">
                                    <Chip 
                                        label={clientData.estado}
                                        color={clientData.estado === 'ACTIVO' ? 'success' : 'error'}
                                        size="small"
                                    />
                                </Typography>
                            </Box>
                        )}

                        {/* Actividades (only if from AFIP) */}
                        {isAfipClient && clientData.actividad && clientData.actividad.length > 0 && (
                            <Box>
                                <Typography variant="caption" color="text.secondary">
                                    Actividades Registradas
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    {clientData.actividad.slice(0, 2).map((act, index) => (
                                        <Typography key={index} variant="body2" sx={{ fontSize: '0.85rem' }}>
                                            • {act.descripcion}
                                        </Typography>
                                    ))}
                                    {clientData.actividad.length > 2 && (
                                        <Typography variant="caption" color="text.secondary">
                                            ... y {clientData.actividad.length - 2} más
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Warning for mismatched invoice types */}
                {clientData.condicion_impositiva === 'RESPONSABLE INSCRIPTO' && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Este cliente es <strong>Responsable Inscripto</strong>. Debe emitir <strong>Factura A</strong>.
                        </Typography>
                    </Alert>
                )}
                {(clientData.condicion_impositiva === 'MONOTRIBUTO' || clientData.condicion_impositiva === 'EXENTO') && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Este cliente es <strong>{clientData.condicion_impositiva}</strong>. Debe emitir <strong>Factura B</strong>.
                        </Typography>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9', justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={isSaving}
                >
                    Cancelar
                </Button>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Only show "Select without save" for AFIP clients */}
                    {isAfipClient && (
                        <Button
                            variant="outlined"
                            startIcon={<CheckIcon />}
                            onClick={handleSelectWithoutSave}
                            disabled={isSaving}
                        >
                            Seleccionar sin Guardar
                        </Button>
                    )}

                    {/* Show different button text for local vs AFIP */}
                    <Button
                        variant="contained"
                        startIcon={isLocalClient ? <CheckIcon /> : <SaveIcon />}
                        onClick={isLocalClient ? handleSelectWithoutSave : handleSelectAndSave}
                        disabled={isSaving}
                        sx={{ 
                            backgroundColor: isLocalClient ? '#4caf50' : '#2196f3',
                            '&:hover': { 
                                backgroundColor: isLocalClient ? '#388e3c' : '#1976d2'
                            }
                        }}
                    >
                        {isSaving 
                            ? 'Guardando...' 
                            : isLocalClient 
                                ? 'Seleccionar' 
                                : 'Guardar y Seleccionar'
                        }
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

ClientSelectionModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    clientData: PropTypes.shape({
        source: PropTypes.oneOf(['local', 'afip']).isRequired,
        documento_nro: PropTypes.string.isRequired,
        documento_tipo: PropTypes.string,
        razon_social: PropTypes.string.isRequired,
        condicion_impositiva: PropTypes.string.isRequired,
        direccion: PropTypes.string,
        localidad: PropTypes.string,
        provincia: PropTypes.string,
        codigopostal: PropTypes.string,
        estado: PropTypes.string,
        actividad: PropTypes.arrayOf(PropTypes.shape({
            descripcion: PropTypes.string,
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            nomenclador: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            periodo: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        }))
    }),
    onSelectWithoutSave: PropTypes.func.isRequired,
    onSelectAndSave: PropTypes.func.isRequired
};

export default ClientSelectionModal;
